import * as React from 'react'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { useFormikContext } from 'formik'
import { FamilyLawModuleDto, RequestDto } from '../../../../../hooks/module/useFamilyLaw'
import { useThemeClassName_unstable } from '@fluentui/react-shared-contexts'
import { RelationshipType } from '../../Relationship'
import { Button, Field } from '@fluentui/react-components'
import { AddCircleRegular, DeleteRegular } from '@fluentui/react-icons'
import LocalDatePicker from '../../../../../components/DatePicker/LocalDatePicker'
import { ProvisionalRequestOptions, ProvisionalType, ProvisionalTypeMap, RequestType, RequestTypeMap } from '../Requests'
import Decree from './Decree'
import { TreeItem, TreeItemLayout } from '@fluentui/react-components/unstable'
import useViewport, { Viewport } from '../../../../../hooks/useViewport'
import BaseCombobox from '../../../../Combobox/BaseCombobox'
import Textarea from '../../../../Text/TextArea'

type DialogProps = {
    request: RequestDto
    setRequest: (request: RequestDto) => void
    styles: Record<'container', string>
}
const defaultText = 'Vul verzoek in'

function RequestItem(props): ReactElement {
    return (
        <TreeItem itemType={'leaf'}>
            <TreeItemLayout actions={<Button size="small" appearance="transparent" icon={<DeleteRegular />} onClick={() => props.remove()} />}>
                {ProvisionalTypeMap.get(props.type)}
            </TreeItemLayout>
        </TreeItem>
    )
}

function MeritItem(props): ReactElement {
    const { values } = useFormikContext<FamilyLawModuleDto>()
    const { remove, request, index, onChange } = props
    const baseRequestContent = useMemo(() => {
        switch (values.relationshipType) {
            case RelationshipType.Marriage:
                return 'Echtscheiding: '
            case RelationshipType.Registered:
                return 'Ontbinding geregistreerd partnerschap: '
            default:
                return ''
        }
    }, [values.relationshipType])

    return (
        <TreeItem itemType={'leaf'}>
            <TreeItemLayout actions={<Button size="small" appearance="transparent" icon={<DeleteRegular />} onClick={() => remove(index)} />}>
                <Textarea
                    key={index}
                    autoAdjustHeight
                    value={request.meritContent[index]?.trim() ? request.meritContent[index] : baseRequestContent}
                    onChange={(_, data) => onChange(index, data.value)}
                />
            </TreeItemLayout>
        </TreeItem>
    )
}

export default function Body(props: DialogProps): ReactElement {
    const { values } = useFormikContext<FamilyLawModuleDto>()
    const { isViewport, viewport } = useViewport()
    const useColumns = useMemo(() => isViewport('>=', Viewport.OutlookNewWide), [viewport])
    const request = props.request
    const themeClassName = useThemeClassName_unstable()
    const getRequestType = (requestType: string) => {
        const enumValue = Object.keys(RequestType).find(key => RequestType[key] === requestType)
        return RequestType[enumValue]
    }
    const [requestType, setRequestType] = useState<RequestType>(getRequestType(request.type))
    const selectableTypeOptions = ProvisionalRequestOptions.filter(option => !request.provisionalContent?.includes(option.key))
    const removeProvisionalContent = (index: number) => {
        props.setRequest({
            ...request,
            provisionalContent: request.provisionalContent.filter((_, order) => order !== index),
        })
    }

    const removeMeritContent = (index: number) => {
        props.setRequest({
            ...request,
            meritContent: request.meritContent.filter((_, order) => order !== index),
        })
    }

    const editMeritContent = (index: number, text: string) => {
        const copyArray = [...request.meritContent]
        copyArray[index] = text
        props.setRequest({ ...request, meritContent: copyArray })
    }

    useEffect(() => {
        if (request.type === RequestType.Merit) {
            switch (values.relationshipType) {
                case RelationshipType.Marriage:
                    if (!request.meritContent.length) {
                        props.setRequest({
                            ...request,
                            meritContent: ['Echtscheiding: '],
                        })
                    }
                    break
                case RelationshipType.Registered:
                    if (!request.meritContent.length) {
                        props.setRequest({ ...request, meritContent: ['Ontbinding geregistreerd partnerschap: '] })
                    }
                    break
                default:
                    if (!request.meritContent.length) {
                        props.setRequest({ ...request, meritContent: ['Vul verzoek in'] })
                    }
                    break
            }
        } else {
            props.setRequest({ ...request, provisionalContent: request.provisionalContent || [] })
        }
    }, [values.relationshipType, request.type])

    return (
        <div className={props.styles.container}>
            <Field label="Type procedure">
                <BaseCombobox
                    onOptionSelect={(_, data) => {
                        props.setRequest({ ...request, type: data.optionValue })
                        const requestType = getRequestType(data.optionValue)
                        setRequestType(requestType)
                    }}
                    selectedOptions={[request.type]}
                    entities={Object.values(RequestType)}
                    getEntityLabel={type => RequestTypeMap.get(type)}
                    getEntityValue={type => type}
                />
            </Field>
            {requestType && (
                <Field label="Inhoud verzoek">
                    {requestType === RequestType.Provisional ? (
                        <>
                            <BaseCombobox
                                onOptionSelect={(_, data) =>
                                    props.setRequest({
                                        ...request,
                                        provisionalContent: Object.values(ProvisionalType).filter(option =>
                                            [...request.provisionalContent, data.optionValue].some(value => value === option.toString()),
                                        ),
                                    })
                                }
                                selectedOptions={['']}
                                entities={selectableTypeOptions}
                                getEntityLabel={entity => entity.text}
                                getEntityValue={entity => entity.key}
                            />
                            {request.provisionalContent.map((type, index) => (
                                <RequestItem key={index} type={type} remove={() => removeProvisionalContent(index)} />
                            ))}
                        </>
                    ) : (
                        <div>
                            <div key="merit" className={props.styles.container}>
                                {request.meritContent.map((_, index) => (
                                    <MeritItem
                                        key={index}
                                        request={request}
                                        index={index}
                                        remove={() => removeMeritContent(index)}
                                        onChange={editMeritContent}
                                    />
                                ))}
                            </div>
                            <Button
                                key={'button'}
                                size="small"
                                appearance="transparent"
                                icon={<AddCircleRegular />}
                                onClick={() =>
                                    props.setRequest({
                                        ...request,
                                        meritContent: [...request.meritContent, defaultText],
                                    })
                                }
                            >
                                Verzoek toevoegen
                            </Button>
                        </div>
                    )}
                </Field>
            )}
            <Field label="Peildatum">
                <LocalDatePicker
                    popupSurface={{ className: themeClassName }}
                    isoValue={request.referenceDate}
                    onSelectDate={date => props.setRequest({ ...request, referenceDate: date })}
                />
            </Field>
            <Decree {...props} useColumns={useColumns} />
        </div>
    )
}
